import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronDown, Check } from 'lucide-react';
import { COUNTRY_CODES } from '@/lib/constants/countryCodes';
import { MAX_PHONE_DIGITS } from '@/lib/constants/phoneValidation';
import { cn } from '@/lib/utils';

/**
 * Convert a flag emoji (two regional-indicator code points, e.g. "🇮🇳") to its
 * ISO-3166 alpha-2 code ("in"). We use this to load a real SVG flag, because
 * Windows renders flag emoji as plain letters ("IN") — so an emoji-only flag
 * looks broken there. Returns '' when the input isn't a valid flag emoji.
 */
function isoFromFlagEmoji(flag: string): string {
  try {
    const letters = Array.from(flag)
      .map((c) => c.codePointAt(0) || 0)
      .filter((cp) => cp >= 0x1f1e6 && cp <= 0x1f1ff)
      .map((cp) => String.fromCharCode(cp - 0x1f1e6 + 65));
    return letters.length === 2 ? letters.join('').toLowerCase() : '';
  } catch {
    return '';
  }
}

/** A country flag that works cross-platform: SVG image, with the emoji as a
 * graceful fallback if the ISO can't be derived or the image fails to load. */
function Flag({ flag }: { flag: string }) {
  const iso = isoFromFlagEmoji(flag);
  const [failed, setFailed] = useState(false);
  if (!iso || failed) {
    return <span className="text-base leading-none">{flag}</span>;
  }
  return (
    <img
      src={`https://flagcdn.com/${iso}.svg`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-4 w-[22px] shrink-0 rounded-[2px] object-cover"
    />
  );
}

interface PhoneNumberInputProps {
  countryCode: string;
  phoneDisplay: string;
  onCountryChange: (code: string) => void;
  onPhoneChange: (digits: string) => void;
  invalid?: boolean;
  id?: string;
  autoFocus?: boolean;
  placeholder?: string;
  /** Extra classes for the outer control — e.g. `h-12` to match tall form fields. */
  className?: string;
}

/**
 * Single combined phone field: [flag +code] │ [number]. The country picker and
 * the number input live inside ONE bordered control with a divider between
 * them. `onPhoneChange` always receives cleaned digits (max length enforced).
 */
export function PhoneNumberInput({
  countryCode,
  phoneDisplay,
  onCountryChange,
  onPhoneChange,
  invalid = false,
  id,
  autoFocus,
  placeholder = 'Your phone number',
  className,
}: PhoneNumberInputProps) {
  const [open, setOpen] = useState(false);
  const selected = COUNTRY_CODES.find((c) => c.code === countryCode);

  return (
    <div
      className={cn(
        'flex h-10 w-full items-center rounded-md border border-input bg-background transition-colors',
        'focus-within:border-input-focus focus-within:ring-1 focus-within:ring-input-focus',
        invalid && 'border-red-400 focus-within:border-red-400 focus-within:ring-red-400',
        className,
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label="Select country code"
            className="flex h-full items-center gap-1.5 rounded-l-md pl-3 pr-2 text-sm outline-none transition-colors hover:bg-muted/40 focus-visible:bg-muted/40"
          >
            <Flag flag={selected?.flag || '🇮🇳'} />
            <span className="font-medium">{countryCode}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0 max-h-[400px] overflow-hidden" align="start">
          <Command onWheel={(e) => e.stopPropagation()} className="rounded-lg">
            <div className="px-3 pt-3 pb-2 border-b bg-muted/30">
              <CommandInput placeholder="Search country..." className="h-9 text-sm" />
            </div>
            <CommandList className="max-h-[300px] overflow-y-auto">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRY_CODES.map((country) => (
                  <CommandItem
                    // Dial codes repeat (e.g. +1 for US & Canada) — key on both.
                    key={`${country.code}-${country.country}`}
                    value={`${country.country} ${country.code}`}
                    onSelect={() => {
                      onCountryChange(country.code);
                      setOpen(false);
                    }}
                    className="cursor-pointer hover:bg-accent"
                  >
                    <Check className={cn('mr-2 h-4 w-4', countryCode === country.code ? 'opacity-100' : 'opacity-0')} />
                    <Flag flag={country.flag} />
                    <span className="ml-2 flex-1">{country.country}</span>
                    <span className="text-muted-foreground text-sm">{country.code}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Separator ("|") between the country code and the number. */}
      <div className="h-5 w-px shrink-0 bg-border" aria-hidden="true" />

      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoFocus={autoFocus}
        maxLength={MAX_PHONE_DIGITS}
        placeholder={placeholder}
        aria-invalid={invalid}
        value={phoneDisplay}
        onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS))}
        className="no-autofill-bg h-full min-w-0 flex-1 rounded-r-md bg-transparent px-3 text-base outline-none placeholder:text-muted-foreground md:text-sm"
      />
    </div>
  );
}
